import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { qk } from "@/api/queryKeys";
import { platform, type PurchaseResult } from "@/platform";
import { t } from "@/i18n/ko";
import { onboardingStep } from "@/app/onboarding";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";
import { claimPurchase, isPurchasing, releasePurchase } from "./purchaseClaims";
import { submitPurchase } from "./submitPurchase";

/**
 * 강제 종료·네트워크 실패로 지급되지 않은 구매 복구(AC0-179). 회원일 때 시작 시·포그라운드 복귀 시 미처리 구매를 다시 제출하고,
 * iOS 는 Transaction.updates 로 온 거래도 제출한다. 서버가 granted 면 잔액 반영 + 안내, already_granted 면 조용히 finish 만.
 * 실패(네트워크·402 등)는 finish 하지 않고 남겨 다음 기회에 다시 시도한다.
 * 온보딩(약관·생년월일·프로필)이 끝나기 전엔 돌리지 않는다 — 서버 gate 가 403 으로 거절해 헛요청만 쌓인다(AC0-281).
 */
export function usePurchaseRecovery() {
  const isMember = useAuthStore((s) => s.status === "member" && onboardingStep(s.user) === null);
  const qc = useQueryClient();

  useEffect(() => {
    if (!isMember) return;
    let active = true;

    const recover = async (p: PurchaseResult) => {
      if (!claimPurchase(p)) return;
      try {
        const res = await submitPurchase(p);
        await platform.billing.finish(p).catch(() => undefined);
        if (!active || res.status !== "granted") return;
        useAuthStore.getState().setBalance(res.balance);
        void qc.invalidateQueries({ queryKey: qk.bricks });
        void qc.invalidateQueries({ queryKey: qk.me });
        void qc.invalidateQueries({ queryKey: ["transactions"] });
        useUiStore.getState().showSnackbar(t("store.purchase.recovered", { bricks: res.purchase.bricks.toLocaleString("ko-KR") }));
      } catch {
        releasePurchase(p);
      }
    };
    const scan = () => {
      if (isPurchasing()) return;
      void platform.billing
        .getPendingPurchases()
        .then((list) => list.forEach((p) => void recover(p)))
        .catch(() => undefined);
    };

    scan();
    const offUpdated = platform.billing.onPurchaseUpdated((p) => void recover(p));
    const offForeground = platform.app.onForeground(scan);
    return () => {
      active = false;
      offUpdated();
      offForeground();
    };
  }, [isMember, qc]);
}
